import {
  Button,
  Form,
  Input,
  Space,
  type ButtonProps,
  type FormItemProps,
  type InputProps,
} from "antd";
import type { ReactNode } from "react";

export type PickerFormFieldProps = Pick<
  FormItemProps,
  "extra" | "help" | "label" | "required" | "style" | "tooltip" | "validateStatus"
> & {
  control: ReactNode;
  pickButtonText: ReactNode;
  onPick: () => void;
  pickButtonProps?: ButtonProps;
  clearButtonText?: ReactNode;
  onClear?: () => void;
  clearButtonProps?: ButtonProps;
};

export type PickerInlineControlProps = {
  control: ReactNode;
  pickButtonText: ReactNode;
  onPick: () => void;
  pickButtonProps?: ButtonProps;
  clearButtonText?: ReactNode;
  onClear?: () => void;
  clearButtonProps?: ButtonProps;
  block?: boolean;
};

type PickerDisplayInputBaseProps = Omit<InputProps, "readOnly" | "value">;

export function toControlledPickerValueProps(value: unknown) {
  return { value: value ?? "" };
}

export type PickerHiddenInputProps = Omit<FormItemProps, "children" | "getValueProps" | "hidden">;

export function PickerHiddenInput(props: PickerHiddenInputProps) {
  return (
    <Form.Item {...props} hidden getValueProps={toControlledPickerValueProps}>
      <input type="hidden" />
    </Form.Item>
  );
}

export type PickerDisplayInputProps =
  | (PickerDisplayInputBaseProps & {
      displayName: FormItemProps["name"];
      value?: never;
    })
  | (PickerDisplayInputBaseProps & {
      displayName?: undefined;
      value?: string;
    });

export function PickerDisplayInput(props: PickerDisplayInputProps) {
  if ("displayName" in props && props.displayName !== undefined) {
    const { displayName, ...inputProps } = props;
    return (
      <Form.Item name={displayName} noStyle getValueProps={toControlledPickerValueProps}>
        <Input {...inputProps} readOnly />
      </Form.Item>
    );
  }

  const { value, ...inputProps } = props;
  return <Input {...inputProps} readOnly value={value ?? ""} />;
}

function renderPickerButtons({
  pickButtonText,
  onPick,
  pickButtonProps,
  clearButtonText,
  onClear,
  clearButtonProps,
}: Omit<PickerInlineControlProps, "control" | "block">) {
  return (
    <>
      <Button onClick={onPick} {...pickButtonProps}>
        {pickButtonText}
      </Button>
      {onClear && clearButtonText ? (
        <Button onClick={onClear} {...clearButtonProps}>
          {clearButtonText}
        </Button>
      ) : null}
    </>
  );
}

// Shared picker affordance for field-level and row-level "find and return" entry points.
export function PickerInlineControl({
  control,
  pickButtonText,
  onPick,
  pickButtonProps,
  clearButtonText,
  onClear,
  clearButtonProps,
  block = true,
}: PickerInlineControlProps) {
  return (
    <Space.Compact block={block}>
      {control}
      {renderPickerButtons({
        pickButtonText,
        onPick,
        ...(pickButtonProps === undefined ? {} : { pickButtonProps }),
        clearButtonText,
        ...(onClear === undefined ? {} : { onClear }),
        ...(clearButtonProps === undefined ? {} : { clearButtonProps }),
      })}
    </Space.Compact>
  );
}

export function PickerFormField({
  control,
  pickButtonText,
  onPick,
  pickButtonProps,
  clearButtonText,
  onClear,
  clearButtonProps,
  ...formItemProps
}: PickerFormFieldProps) {
  return (
    <Form.Item {...formItemProps}>
      <PickerInlineControl
        control={control}
        pickButtonText={pickButtonText}
        onPick={onPick}
        {...(pickButtonProps === undefined ? {} : { pickButtonProps })}
        clearButtonText={clearButtonText}
        {...(onClear === undefined ? {} : { onClear })}
        {...(clearButtonProps === undefined ? {} : { clearButtonProps })}
      />
    </Form.Item>
  );
}
